import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { getTodayReadings, type TodayReadings } from "@/lib/api";

const POLL_MS = 30_000;

interface TodayVitalsState {
    data: TodayReadings | null;
    reload: () => void;
    /** Recorded time of the set the open prescription already holds (filled in or dismissed) */
    appliedAt: string | null;
    setAppliedAt: (at: string | null) => void;
}

const TodayVitalsContext = createContext<TodayVitalsState | null>(null);

/**
 * Loads a patient's vitals recorded today and re-checks every 30 s and on window focus, so a set the
 * nurse records during the consultation reaches the doctor. Returns null data while disabled.
 */
export function useTodayVitalsSource(patientId: string | undefined, enabled = true) {
    const [data, setData] = useState<TodayReadings | null>(null);
    const reload = useCallback(() => {
        if (!patientId || !enabled) return;
        getTodayReadings(patientId).then(setData).catch(() => {});
    }, [patientId, enabled]);
    useEffect(() => {
        setData(null);
        reload();
        if (!patientId || !enabled) return;
        const timer = setInterval(() => { if (document.visibilityState === "visible") reload(); }, POLL_MS);
        window.addEventListener("focus", reload);
        return () => { clearInterval(timer); window.removeEventListener("focus", reload); };
    }, [reload, patientId, enabled]);
    return { data, reload };
}

/** One source of today's vitals for the patient page: the header strip and the prescription's vitals section. */
export function TodayVitalsProvider({ patientId, children }: { patientId: string; children: ReactNode }) {
    const { data, reload } = useTodayVitalsSource(patientId);
    const [appliedAt, setAppliedAt] = useState<string | null>(null);
    const value = useMemo(() => ({ data, reload, appliedAt, setAppliedAt }), [data, reload, appliedAt]);
    return <TodayVitalsContext.Provider value={value}>{children}</TodayVitalsContext.Provider>;
}

/** The page's shared source, or null outside a provider (e.g. the edit-prescription page) */
export function useTodayVitalsContext() {
    return useContext(TodayVitalsContext);
}
